import { ComponentRef, Inject, Injectable, Injector, Type, ViewContainerRef } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Observable, ReplaySubject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { ISidebarOptions } from '@interfaces/sidebar-options';
import { shareReplay } from 'rxjs/operators';

/**
 * While a sidebar of either slot is open, the CDK overlay container (tooltips, dropdowns, modals; z-index 1000) carries this
 * class and sits above the sidebar — an as-window (`.md` 1001) or a custom z-index (Quick Fix 1031, line panel 1032) would hide
 * the overlays opened from inside it otherwise (#13548). The class is styled in `overrides/nztooltip.scss`.
 */
const OVERLAY_OVER_SIDEBAR_CLASS: string = 'over-sidebar';

@Injectable({ providedIn: 'root' })
export class AppService {
  public componentSidebarRef: ComponentRef<any> = null;
  public componentRef: ComponentRef<any> = null;
  public sidebarFormRef: ViewContainerRef;

  public componentSidebarAsWindowRef: ComponentRef<any> = null;
  public sidebarAsWindowFormRef: ViewContainerRef;

  public withScrollingBodyClass: string = 'with-scrolling-sidebar';
  public withNoScrollingBodyClass: string = 'with-no-scrolling-sidebar';

  public isOpen$: ReplaySubject<boolean> = new ReplaySubject();
  public isLg$: ReplaySubject<boolean> = new ReplaySubject();
  public isXlg$: ReplaySubject<boolean> = new ReplaySubject();
  public isPurple$: ReplaySubject<boolean> = new ReplaySubject();
  public isWindowMd$: ReplaySubject<boolean> = new ReplaySubject();
  public options$: ReplaySubject<ISidebarOptions> = new ReplaySubject();

  public isOpenSidebarAsWindow$: ReplaySubject<boolean> = new ReplaySubject();
  public wchatUnderSidebar$: ReplaySubject<boolean> = new ReplaySubject();
  public optionsSidebarAsWindow$: ReplaySubject<ISidebarOptions> = new ReplaySubject();

  private isClickOnClose$: ReplaySubject<boolean> = new ReplaySubject();
  /** The subjects above replay but hold no value: the open state is tracked here for the overlay layer (#13548). */
  private isSidebarOpen: boolean = false;
  private isSidebarAsWindowOpen: boolean = false;

  constructor(@Inject(DOCUMENT) private document: Document, private overlayContainer: OverlayContainer) {}

  public setSidebarForm(form: ViewContainerRef): void {
    this.sidebarFormRef = form;
  }

  public get sidebarForm(): ViewContainerRef {
    return this.sidebarFormRef;
  }

  public get isClickOnCloseSubject(): Observable<boolean> {
    return this.isClickOnClose$.pipe(shareReplay(1));
  }

  public setWchatUnderSidebar(val: boolean): void {
    this.wchatUnderSidebar$.next(val);
  }

  public get wchatUnderSidebar(): Observable<boolean> {
    return this.wchatUnderSidebar$;
  }

  public openSidebar(componentRef: Type<any>, options: ISidebarOptions, injector?: Injector): ComponentRef<any> {
    this.closeSidebar();

    if (!options?.isEnableScrolling) {
      this.document.body.classList.add(this.withNoScrollingBodyClass);
    } else {
      this.document.body.classList.add(this.withScrollingBodyClass);
    }

    if (options?.isLg) {
      this.isLg$.next(true);
    }
    if (options?.isXlg) {
      this.isXlg$.next(true);
    }
    if (options?.isPurple) {
      this.isPurple$.next(true);
    }

    this.isOpen$.next(true);
    this.isSidebarOpen = true;
    this.syncOverlayLayer();
    this.options$.next(options);
    const res: ComponentRef<any> = this.sidebarForm.createComponent(componentRef, injector ? { injector } : undefined);
    this.componentRef = res;

    this.isClickOnClose$ = new ReplaySubject();

    this.setWchatUnderSidebar(true);

    return res;
  }

  public closeSidebar(isClickOnClose?: boolean): void {
    this.componentRef?.destroy();
    this.isOpen$.next(false);
    this.isSidebarOpen = false;
    this.syncOverlayLayer();
    this.isLg$.next(false);
    this.isXlg$.next(false);
    this.isPurple$.next(false);
    this.isClickOnClose$.next(false);
    this.document.body.classList.remove(this.withNoScrollingBodyClass);
    this.document.body.classList.remove(this.withScrollingBodyClass);

    if (!!isClickOnClose) {
      this.isClickOnClose$.next(true);
    }

    this.isClickOnClose$.complete();

    this.setWchatUnderSidebar(false);
  }

  /* -- used for batch imports -- */
  public setSidebarAsWindowForm(form: ViewContainerRef): void {
    this.sidebarAsWindowFormRef = form;
  }

  public setSidebarOptions(options: ISidebarOptions) {
    this.options$.next(options);
  }

  public get sidebarAsWindowForm(): ViewContainerRef {
    return this.sidebarAsWindowFormRef;
  }

  public openSidebarAsWindow(componentRef: Type<any>, options: ISidebarOptions): ComponentRef<any> {
    this.closeSidebarAsWindow();

    if (options?.isMd) {
      this.isWindowMd$.next(true);
    }

    if (!options?.isEnableScrolling) {
      this.document.body.classList.add(this.withNoScrollingBodyClass);
    } else {
      this.document.body.classList.add(this.withScrollingBodyClass);
    }

    this.isOpenSidebarAsWindow$.next(true);
    this.isSidebarAsWindowOpen = true;
    this.syncOverlayLayer();
    this.optionsSidebarAsWindow$.next(options);
    this.componentSidebarAsWindowRef = this.sidebarAsWindowForm.createComponent(componentRef);

    this.setWchatUnderSidebar(true);

    return this.componentSidebarAsWindowRef;
  }

  public closeSidebarAsWindow(): void {
    this.componentSidebarAsWindowRef?.destroy();
    this.isOpenSidebarAsWindow$.next(false);
    this.isSidebarAsWindowOpen = false;
    this.syncOverlayLayer();
    this.optionsSidebarAsWindow$.next(null);
    this.isWindowMd$.next(false);
    this.document.body.classList.remove(this.withNoScrollingBodyClass);
    this.document.body.classList.remove(this.withScrollingBodyClass);

    this.setWchatUnderSidebar(false);
  }

  /** The overlay container stays above the sidebars as long as one of the two slots is open (#13548). */
  private syncOverlayLayer(): void {
    const classList: DOMTokenList = this.overlayContainer.getContainerElement().classList;
    if (this.isSidebarOpen || this.isSidebarAsWindowOpen) {
      classList.add(OVERLAY_OVER_SIDEBAR_CLASS);
    } else {
      classList.remove(OVERLAY_OVER_SIDEBAR_CLASS);
    }
  }
}
